import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import { MENU } from "../../data/content";
import { useOrder } from "../../context/OrderContext";
import { useCart } from "../../context/CartContext";
import { useAvailability } from "../../hooks/useAvailability";
import { Reveal } from "./Reveal";

const Item = ({ item, soldOut }) => {
  const { add } = useCart();
  return (
  <div className="group flex gap-4 border-b border-white/8 py-5" data-testid="menu-item">
    {item.img && (
      <div className="hidden sm:block h-20 w-20 shrink-0 overflow-hidden rounded-xl border border-white/10">
        <img src={item.img} alt={item.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
      </div>
    )}
    <div className="flex-1">
      <div className="flex flex-wrap items-center gap-2">
        <h4 className="font-display text-lg font-semibold text-cream group-hover:text-gold transition-colors">{item.name}</h4>
        {item.badge && (
          <span className="rounded-full bg-ember/20 border border-ember/40 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-ember">
            {item.badge}
          </span>
        )}
      </div>
      <p className="mt-1 max-w-xl text-sm text-cream/55 leading-relaxed">{item.desc}</p>
      <div className="mt-2.5 flex flex-wrap gap-2">
        {item.prices.map(([label, val], i) => {
          const id = `${item.name}-${label}`.toLowerCase().replace(/[^a-z0-9]+/g, "-");
          if (soldOut.has(id)) {
            return (
              <span key={i} data-testid={`soldout-${id}`}
                className="inline-flex cursor-not-allowed items-center gap-1.5 rounded-full border border-red-400/30 bg-red-500/10 px-3 py-1.5 text-sm font-semibold text-red-300/80">
                <span className="font-medium">{label}</span> Sold out
              </span>
            );
          }
          return (
          <button
            key={i}
            onClick={() => add(item.name, label, val)}
            data-testid={`add-${id}`}
            className="group/btn inline-flex items-center gap-1.5 rounded-full border border-white/12 bg-white/[0.03] px-3 py-1.5 text-sm font-semibold text-cream transition-all hover:-translate-y-0.5 hover:border-gold/50 hover:bg-gold/10"
          >
            <span className="text-cream/40 font-medium">{label}</span>
            <span className="text-gold">${val}</span>
            <Plus size={13} className="text-cream/40 group-hover/btn:text-gold" />
          </button>
          );
        })}
      </div>
    </div>
  </div>
  );
};

export const FullMenu = () => {
  const { openOrder } = useOrder();
  const { count, openCheckout } = useCart();
  const soldOut = useAvailability();
  const [active, setActive] = useState(MENU[0].section);
  const activeSection = MENU.find((s) => s.section === active) || MENU[0];

  return (
    <section id="menu" className="relative mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32 scroll-mt-20">
      <div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:gap-16">
        {/* Left rail */}
        <div className="lg:sticky lg:top-24 lg:w-72 shrink-0">
          <Reveal>
            <p className="text-[11px] uppercase tracking-[0.24em] text-gold font-semibold">The Full Menu</p>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl font-semibold tracking-tight text-cream">
              Everything's <span className="text-gold-gloss">100% Halal</span>
            </h2>
          </Reveal>

          <div className="mt-8 -mx-5 flex gap-2 overflow-x-auto px-5 pb-2 lg:mx-0 lg:flex-col lg:overflow-visible lg:px-0">
            {MENU.map((s) => (
              <button
                key={s.section}
                onClick={() => setActive(s.section)}
                data-testid={`menu-tab-${s.section.replace(/[^a-z]/gi, "").toLowerCase()}`}
                className={`whitespace-nowrap rounded-full px-4 py-2.5 text-left text-sm font-semibold transition-colors lg:rounded-xl ${
                  active === s.section
                    ? "bg-gold text-ink"
                    : "border border-white/10 text-cream/65 hover:text-cream hover:border-gold/30 lg:border-transparent lg:hover:bg-white/5"
                }`}
              >
                {s.section}
              </button>
            ))}
          </div>

          <button
            onClick={count > 0 ? openCheckout : openOrder}
            data-testid="menu-order-now"
            className="mt-8 hidden lg:inline-flex items-center rounded-full bg-gold px-6 py-3.5 text-sm font-semibold text-ink transition-transform hover:-translate-y-0.5 gold-glow"
          >
            {count > 0 ? `Checkout · ${count} item${count > 1 ? "s" : ""}` : "Order Now"}
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 min-w-0">
          <AnimatePresence mode="wait">
            <motion.div
              key={active}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            >
              <h3 className="font-display text-2xl font-semibold text-cream/90">{activeSection.section}</h3>
              <div className="mt-4">
                {activeSection.items.map((it, i) => (
                  <Item key={i} item={it} soldOut={soldOut} />
                ))}
              </div>
              {activeSection.note && (
                <p className="mt-5 rounded-2xl bg-white/[0.03] border border-white/8 px-4 py-3 text-xs sm:text-sm text-cream/55">
                  {activeSection.note}
                </p>
              )}
            </motion.div>
          </AnimatePresence>

          <button
            onClick={count > 0 ? openCheckout : openOrder}
            className="mt-10 inline-flex w-full items-center justify-center rounded-full bg-gold px-6 py-4 text-base font-semibold text-ink transition-transform hover:-translate-y-0.5 lg:hidden"
          >
            {count > 0 ? `Checkout · ${count} item${count > 1 ? "s" : ""}` : "Order Now"}
          </button>
        </div>
      </div>
    </section>
  );
};

export default FullMenu;
